import { useAccount } from "@/context/account";
import { useMe } from "@/context/me";
import { useApolloClient } from "@apollo/client/react";
import { debounce } from "lodash-es";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { DealerCustomersGlobalSearchQuery } from "@/gql/graphql";
import { DealerUserRole } from "@/gql/graphql";
import { fetchContracts } from "@/components/app/Navigation/GlobalHeader/GlobalSearch/services/fetchContracts";
import { fetchDevices } from "@/components/app/Navigation/GlobalHeader/GlobalSearch/services/fetchDevices";
import type {
  ContractAutocompleteOptions,
  CustomerSearchItems,
  DealerAutocompleteOptions,
  DevicesAutocompleteOptions,
  InvoiceAutocompleteOptions,
} from "@/components/Navigation/GlobalHeader/GlobalSearch/types";
import { fetchDealers } from "@/components/app/Navigation/GlobalHeader/GlobalSearch/services/fetchDealers";
import { fetchInvoices } from "@/components/app/Navigation/GlobalHeader/GlobalSearch/services/fetchInvoices";
import {
  filterDealerCustomersForSearch,
  loadDealerCustomersList,
} from "@/components/app/Navigation/GlobalHeader/GlobalSearch/services/fetchCustomers";

async function fetchResultsForCategory(
  searchCategory: string,
  client: ReturnType<typeof useApolloClient>,
  accountId: string,
  searchTerm: string,
  excludeSalesOrderAdminCreatedContracts: boolean,
  signal?: AbortSignal
): Promise<{
  totalCount: number;
  items:
    | ContractAutocompleteOptions[]
    | DevicesAutocompleteOptions[]
    | DealerAutocompleteOptions[]
    | InvoiceAutocompleteOptions[]
    | CustomerSearchItems;
}> {
  switch (searchCategory) {
    case "contracts":
      return fetchContracts(
        client,
        accountId,
        searchTerm,
        excludeSalesOrderAdminCreatedContracts,
        signal
      );
    case "devices":
      return fetchDevices(client, accountId, searchTerm, signal);
    case "dealers":
      return fetchDealers(client, accountId, searchTerm, signal);
    case "invoices":
      return fetchInvoices(client, accountId, searchTerm, signal);
    default:
      return { totalCount: 0, items: [] };
  }
}

export function useGlobalSearchSuggestions(
  searchCategoryRef: RefObject<string>
) {
  const client = useApolloClient();
  const { account } = useAccount();
  const { me } = useMe();
  const [options, setOptions] = useState<{
    totalCount: number;
    items:
      | ContractAutocompleteOptions[]
      | DevicesAutocompleteOptions[]
      | DealerAutocompleteOptions[]
      | InvoiceAutocompleteOptions[]
      | CustomerSearchItems;
  }>({ totalCount: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const dealerCustomersCacheRef = useRef<{
    accountId: string;
    rows: DealerCustomersGlobalSearchQuery["dealerCustomers"];
  } | null>(null);

  const runFetch = useCallback(
    async (searchTerm: string) => {
      const requestId = ++requestIdRef.current;
      const isLatest = () => requestId === requestIdRef.current;

      const trimmedTerm = searchTerm.trim();
      if (trimmedTerm.length === 0) {
        abortControllerRef.current?.abort();
        setOptions({ totalCount: 0, items: [] });
        setLoading(false);
        return;
      }

      const isCustomers = searchCategoryRef.current === "customers";

      let controller: AbortController | null = null;
      if (!isCustomers) {
        abortControllerRef.current?.abort();
        controller = new AbortController();
        abortControllerRef.current = controller;
      }

      try {
        if (isCustomers) {
          const cached = dealerCustomersCacheRef.current;
          let rows: DealerCustomersGlobalSearchQuery["dealerCustomers"];
          if (cached?.accountId === account.id) {
            rows = cached.rows;
          } else {
            rows = await loadDealerCustomersList(client, account.id);
            if (!isLatest()) return;
            dealerCustomersCacheRef.current = { accountId: account.id, rows };
          }

          const { totalCount, items } = filterDealerCustomersForSearch(
            rows,
            trimmedTerm
          );
          if (!isLatest()) return;
          setOptions({ totalCount, items });
        } else {
          const { totalCount, items } = await fetchResultsForCategory(
            searchCategoryRef.current,
            client,
            account.id,
            trimmedTerm,
            me.role !== DealerUserRole.SalesOrderAdmin,
            controller?.signal
          );
          if (controller?.signal.aborted || !isLatest()) return;
          setOptions({ totalCount, items });
        }
      } catch (e) {
        if (controller?.signal.aborted || !isLatest()) return;
        console.error("Global search fetch failed", e);
      } finally {
        if (isLatest()) {
          setLoading(false);
        }
      }
    },
    [client, account.id, me.role, searchCategoryRef]
  );

  const debouncedFetch = useMemo(
    () =>
      debounce((term: string) => {
        void runFetch(term);
      }, 300),
    [runFetch]
  );

  useEffect(
    () => () => {
      requestIdRef.current++;
      debouncedFetch.cancel();
      abortControllerRef.current?.abort();
    },
    [debouncedFetch]
  );

  const resetSuggestions = useCallback(() => {
    requestIdRef.current++;
    abortControllerRef.current?.abort();
    dealerCustomersCacheRef.current = null;
    setOptions({ totalCount: 0, items: [] });
    setLoading(false);
    debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    dealerCustomersCacheRef.current = null;
  }, [account.id]);

  return {
    options,
    setOptions,
    loading,
    setLoading,
    debouncedFetch,
    resetSuggestions,
  };
}
